import { useEffect, useMemo, useState } from "react";
import { CloudFormationSetupInfo } from "restAPI/GenericIntegrationAPI";

import {
  GoogleWorkspaceAPI,
  GwsProduct,
  GwsProductMetadata,
} from "./googleWorkspaceConstants";

interface UseGoogleWorkspaceSetupResult {
  setupInfo: CloudFormationSetupInfo | null;
  productsMetadata: GwsProductMetadata[];
  defaultSelectedProducts: GwsProduct[];
  serviceAccountClientId: string | undefined;
  selectedProducts: GwsProduct[];
  requiredScopes: string[];
  setSelectedProducts: (products: GwsProduct[]) => void;
}

export default function useGoogleWorkspaceSetup(
  initialProducts: GwsProduct[],
): UseGoogleWorkspaceSetupResult {
  const [setupInfo, setSetupInfo] = useState<CloudFormationSetupInfo | null>(
    null,
  );
  const [selectedProducts, setSelectedProducts] =
    useState<GwsProduct[]>(initialProducts);

  useEffect(() => {
    setSelectedProducts(initialProducts);
  }, [initialProducts.join(",")]);

  useEffect(() => {
    const { promise, cancel } = GoogleWorkspaceAPI.getCloudFormationTemplate();
    promise
      .then(
        (
          response: {
            data?: CloudFormationSetupInfo;
          } & CloudFormationSetupInfo,
        ) => setSetupInfo(response?.data || response),
      )
      .catch(() => {});
    return cancel;
  }, []);

  const productsMetadata = useMemo<GwsProductMetadata[]>(() => {
    const raw = setupInfo?.prefilledParameters?.GoogleWorkspaceProductsMetadata;
    if (!raw) return [];
    try {
      return JSON.parse(raw).filter((p: GwsProductMetadata) => p.enabled);
    } catch {
      return [];
    }
  }, [setupInfo]);

  const defaultSelectedProducts = useMemo<GwsProduct[]>(() => {
    const raw = setupInfo?.prefilledParameters?.DefaultSelectedProducts;
    if (!raw) return [];
    try {
      return JSON.parse(raw);
    } catch {
      return [];
    }
  }, [setupInfo]);

  const requiredScopes = useMemo(() => {
    const scopeSet = new Set<string>();
    selectedProducts.forEach((id) => {
      productsMetadata
        .find((p) => p.id === id)
        ?.requiredScopes.forEach((s) => scopeSet.add(s));
    });
    return Array.from(scopeSet);
  }, [selectedProducts, productsMetadata]);

  const serviceAccountClientId =
    setupInfo?.prefilledParameters?.ServiceAccountClientId;

  return {
    setupInfo,
    productsMetadata,
    defaultSelectedProducts,
    serviceAccountClientId,
    selectedProducts,
    requiredScopes,
    setSelectedProducts,
  };
}
